const isRecord = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const isId = value => typeof value === 'string' && /^[a-f\d]{24}$/i.test(value);
const isDate = value => typeof value === 'string' && Number.isFinite(Date.parse(value));
const capabilityKeys = ['canViewTreasury', 'canDecideBudgets', 'canReviewReports', 'canManage', 'canRecordContributions', 'canBookkeep', 'canPrepareReports', 'canAuthorisePayments', 'canConfigure'];
const safe = value => !['password', 'passwordHash', 'privateKey', 'mnemonic', 'accessToken', 'refreshToken'].some(key => Object.hasOwn(value, key));
const isCapabilities = data => capabilityKeys.every(key => typeof data[key] === 'boolean') && (data.canApproveExpenses === undefined || typeof data.canApproveExpenses === 'boolean');
const isHistoryEntry = row => isRecord(row) && safe(row) && ['set_policy', 'grant', 'revoke'].includes(row.action)
  && isId(row.actor) && isDate(row.at) && typeof row.note === 'string'
  && (row.target == null || isId(row.target)) && (row.targetName == null || typeof row.targetName === 'string')
  && (row.action === 'set_policy' ? row.role == null : ['treasurer', 'expense_approver', 'payment_reviewer'].includes(row.role) && typeof row.targetName === 'string' && Boolean(row.targetName.trim()))
  && typeof row.requireSeparateExpenseAuthoriser === 'boolean';
const isAssignment = row => isRecord(row) && safe(row) && isId(row.id) && isId(row.userId)
  && typeof row.name === 'string' && row.name.length > 0 && ['treasurer', 'expense_approver', 'payment_reviewer'].includes(row.role);
const isSettings = data => {
  if (!isRecord(data) || !safe(data) || !isId(data.communityId) || !isCapabilities(data)) return false;
  const canSeeSettings = data.canViewTreasury || data.canManage || data.canAuthorisePayments || data.canApproveExpenses || data.canConfigure;
  if (!canSeeSettings) return !('revision' in data || 'requireSeparateExpenseAuthoriser' in data || 'assignments' in data || 'history' in data || 'nextCursor' in data);
  if (!Number.isSafeInteger(data.revision) || data.revision < 0 || typeof data.requireSeparateExpenseAuthoriser !== 'boolean') return false;
  if (!data.canConfigure) return !('assignments' in data || 'history' in data || 'nextCursor' in data);
  return Array.isArray(data.assignments) && data.assignments.length <= 20 && data.assignments.every(isAssignment)
    && new Set(data.assignments.map(row => row.id)).size === data.assignments.length
    && Array.isArray(data.history) && data.history.length <= 50 && data.history.every(isHistoryEntry)
    && (data.nextCursor === null || isId(data.nextCursor));
};

export class TreasurySettingsResponseContractError extends Error {
  constructor(operation, expected) {
    super(`Invalid treasury settings ${operation} response: expected ${expected}`);
    this.name = 'TreasurySettingsResponseContractError';
    this.code = 'INVALID_API_RESPONSE';
  }
}

const invalid = (operation, expected) => { throw new TreasurySettingsResponseContractError(operation, expected); };

export const readTreasurySettingsAccessResponse = payload => {
  if (!isRecord(payload?.data) || !safe(payload.data) || !isCapabilities(payload.data)
    || ['assignments', 'history', 'revision', 'requireSeparateExpenseAuthoriser', 'nextCursor'].some(key => Object.hasOwn(payload.data, key))) invalid('access', 'all nine capability flags.');
  return payload;
};

export const readTreasurySettingsResponse = payload => {
  if (!isSettings(payload?.data)) invalid('live', 'a permission-filtered settings snapshot.');
  return payload;
};

export const readTreasurySettingsLookupResponse = payload => {
  const data = payload?.data;
  if (!isRecord(data) || !safe(data) || !isId(data.id) || !isId(data.userId) || typeof data.name !== 'string' || !data.name.trim() || !isDate(data.revision)) {
    invalid('member lookup', 'a verified member record with a date revision.');
  }
  return payload;
};

export const readTreasurySettingsActionResponse = payload => {
  if (!isRecord(payload) || typeof payload.replayed !== 'boolean' || !isSettings(payload.data) || payload.data.canConfigure !== true) {
    invalid('action', 'a current configurable settings snapshot and replay status.');
  }
  return payload;
};

export const readTreasurySettingsRecoveryContextResponse = payload => {
  if (!isRecord(payload?.data) || !safe(payload.data) || typeof payload.data.scope !== 'string' || !/^[a-f\d]{24}_[a-f\d]{24}$/i.test(payload.data.scope)) {
    throw new TreasurySettingsResponseContractError('recovery context', 'an authenticated account and community scope');
  }
  return payload;
};

export const readTreasurySettingsRecoveryResponse = payload => {
  if (!isRecord(payload) || !['recorded', 'not_recorded'].includes(payload.outcome)) invalid('recovery', 'a recorded or not-recorded outcome.');
  if (payload.outcome === 'not_recorded') {
    if (payload.data !== null) invalid('recovery', 'a null snapshot for a not-recorded outcome.');
    return payload;
  }
  if (payload.data !== null && (!isSettings(payload.data) || payload.data.canConfigure !== true || payload.data.id !== payload.data.communityId)) {
    invalid('recovery', 'a current configurable settings snapshot or null.');
  }
  return payload;
};

export const isTreasurySettingsSnapshot = isSettings;
